import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getWithdrawalLimit } from '../../api/walletApi';

const cyan = '#00e5ff';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.15)',
  color: '#fff',
};
const th = { color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' };

const WithdrawalLimits = () => {
  const navigate = useNavigate();
  const [limitData, setLimitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getWithdrawalLimit()
      .then((res) => setLimitData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load withdrawal limits.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <button
          onClick={() => navigate('/account-status')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Account Status
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Withdrawal Limits</h5>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
          Your withdrawal limit grows as you activate slots and bring in active direct referrals.
        </div>

        {error && (
          <div style={{ color: '#ff6666', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: cyan }} />
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <Row className="g-3 mb-4">
              {[
                { label: 'Total Limit', value: limitData?.totalLimit, color: '#ffaa44' },
                { label: 'Used Limit', value: limitData?.usedLimit, color: '#ff6666' },
                { label: 'Balance Limit', value: limitData?.availableLimit, color: '#66ff99' },
              ].map(({ label, value, color }) => (
                <Col xs={4} key={label}>
                  <Card style={cardDark} className="p-2 p-md-4 h-100">
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>{label}</div>
                    <div>
                      <span style={{ fontSize: 'clamp(14px, 4vw, 28px)', fontWeight: 800, color }}>{(value ?? 0).toFixed(2)}</span>
                      <span style={{ color: '#aaa', fontSize: 11, marginLeft: 4 }}>USDT</span>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Slot-wise breakdown */}
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Slot-wise Limit Breakdown</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>
              <Table responsive className="mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
                    <th style={{ ...th, whiteSpace: 'nowrap' }}>Slot</th>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <th key={s.planId} style={{ ...th, whiteSpace: 'nowrap', textAlign: 'center' }}>{s.planName}</th>
                    ))}
                    <th style={{ ...th, whiteSpace: 'nowrap', textAlign: 'center', color: '#ffaa44' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>Upgrade</td>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <td key={s.planId} style={{ fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <span style={{ color: s.selfActive ? '#66ff99' : '#555' }}>{s.amount} USDT</span>
                      </td>
                    ))}
                    <td style={{ padding: '14px 20px', border: 'none', textAlign: 'center' }}>—</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>Direct Referrals</td>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <td key={s.planId} style={{ color: cyan, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center' }}>
                        {s.directCount >= 1 ? '1' : ''}
                      </td>
                    ))}
                    <td style={{ color: cyan, fontWeight: 700, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center' }}>
                      {(limitData?.slotBreakdown || []).some((s) => s.directCount >= 1) ? '1' : ''}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>Limit Received</td>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <td key={s.planId} style={{ color: s.limitReceived > 0 ? '#ffaa44' : '#555', fontWeight: 700, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {s.limitReceived > 0 ? `${s.limitReceived.toFixed(2)} USDT` : '—'}
                      </td>
                    ))}
                    <td style={{ color: '#ffaa44', fontWeight: 800, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {(limitData?.slotBreakdown || []).reduce((sum, s) => sum + s.limitReceived, 0).toFixed(2)} USDT
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

          </>
        )}
      </Container>
    </div>
  );
};

export default WithdrawalLimits;
